/**
 * Shared payload builder for the lingo "Word not found" Slack alert.
 *
 * Both the spelling and vocabulary KC detail views fire this when a word the
 * user navigated to genuinely cannot be resolved (after the student's data has
 * finished loading). Centralised here so the alert contract is defined once and
 * can be unit-tested without mounting the heavyweight detail components.
 *
 * The server-side logger (`logClientError`) already enriches the alert with the
 * verified adminId, the full request URL, the environment and a timestamp, so
 * those are intentionally NOT duplicated here.
 */

/** Stable event key — also used as the per-(event+admin) Slack rate-limit key. */
export const WORD_NOT_FOUND_EVENT = "lingo_word_not_found";

export interface WordNotFoundContext {
  /** The student whose KC history was being viewed. */
  studentId: string;
  /** The identifier from the route (`:kcId`) that failed to resolve. */
  wordId: string;
  /** Human-readable word, when known (passed through from the list view). */
  word?: string | null;
  /** Which activity list the word belongs to. */
  listType: "spelling" | "vocabulary";
  /** How many KCs were available to match against (0 ⇒ empty/unloaded data). */
  availableKcCount: number;
  /** Whether the underlying student-data fetch succeeded (false ⇒ likely an API failure, not a true miss). */
  dataFetchOk: boolean;
}

/**
 * Build the `data` payload for `logger.error(WORD_NOT_FOUND_EVENT, ...)`.
 * `message` is surfaced as the Slack alert title ("Word not found").
 */
export function buildWordNotFoundLog(ctx: WordNotFoundContext): Record<string, unknown> {
  return {
    message: "Word not found",
    word: ctx.word || null,
    wordId: ctx.wordId,
    listType: ctx.listType,
    studentId: ctx.studentId,
    availableKcCount: ctx.availableKcCount,
    dataFetchOk: ctx.dataFetchOk,
  };
}
